import { DateTime } from "luxon";
import {
  readClinicalStatusField,
  readLegacyEncounterStatusField,
} from "./encounter-clinical-status.read.util";
import { Appointment } from "../models/admissions.model";
import { SelectableEnumerationItem } from "../models/lookup.model";
import { EncounterStatus, EncounterStatusV2 } from "../services/utils";
import { getWorksheetStatusRawFromAppointment } from "src/app/pages/worksheet/worksheet-patient-status-label.util";

/** Admissions schedule listing: filter options in display order. */
export const ADMISSIONS_SCHEDULE_STATUS_OPTIONS: ReadonlyArray<{
  label: string;
  value: string;
}> = [
  { label: "Scheduled", value: EncounterStatusV2.Scheduled },
  { label: "Open", value: EncounterStatusV2.Open },
  { label: "Retro Auth", value: EncounterStatusV2.RetroAuth },
  { label: "Ready For Billing", value: EncounterStatusV2.ReadyForBilling },
  { label: "Complete", value: EncounterStatus.Complete },
  { label: "Submitted", value: EncounterStatusV2.Submitted },
  { label: "Waitlist", value: EncounterStatusV2.Waitlist },
  { label: "No Show", value: EncounterStatusV2.NoShow },
  { label: "Canceled", value: EncounterStatusV2.Cancelled },
] as const;

export function getAdmissionsScheduleStatusFilterOptions(): SelectableEnumerationItem[] {
  return ADMISSIONS_SCHEDULE_STATUS_OPTIONS.map((opt) => ({
    value: opt.value,
    displayName: opt.label,
    isSelected: false,
  }));
}

/** Keep saved filter lists aligned with the current admissions schedule status catalog. */
export function mergeAdmissionsScheduleStatusFilterOptions(
  existing: SelectableEnumerationItem[] = []
): SelectableEnumerationItem[] {
  const byValue = new Map(
    existing.map((item) => [
      normalizeScheduleStatusKey(String(item.value ?? "")),
      item,
    ])
  );

  return ADMISSIONS_SCHEDULE_STATUS_OPTIONS.map((opt) => {
    const key = normalizeScheduleStatusKey(opt.value);
    const current =
      byValue.get(key) ??
      (key === "scheduled" ? byValue.get("confirmed") : undefined);
    if (current) {
      return {
        ...current,
        value: opt.value,
        displayName: opt.label,
      };
    }
    return {
      value: opt.value,
      displayName: opt.label,
      isSelected: isAdmissionsScheduleStatusSelectedByDefault(opt.value),
    };
  });
}

function normalizeScheduleStatusKey(raw: string): string {
  return (raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[\s_-]+/g, "");
}

/** Map list-clinic / schedule API status to an admissions schedule label, or "" when hidden. */
export function normalizeAdmissionsScheduleStatusDisplay(raw: string): string {
  const key = normalizeScheduleStatusKey(raw);
  if (!key) {
    return "";
  }

  switch (key) {
    case "unconfirmed":
    case "confirmed":
    case "scheduled":
      return EncounterStatusV2.Scheduled;
    case "open":
    case "inprogress":
      return EncounterStatusV2.Open;
    case "draft":
      return EncounterStatus.Draft;
    case "retroauth":
      return EncounterStatusV2.RetroAuth;
    case "readyforbilling":
    case "signreq":
      return EncounterStatusV2.ReadyForBilling;
    case "submitted":
      return EncounterStatusV2.Submitted;
    case "completed":
    case "complete":
      return EncounterStatus.Complete;
    case "waitlist":
      return EncounterStatusV2.Waitlist;
    case "noshow":
      return EncounterStatusV2.NoShow;
    case "cancelled":
    case "canceled":
      return EncounterStatusV2.Cancelled;
    case "preprocedure":
    case "checkedin":
    case "pendingauth":
    case "pendingeligibility":
    case "noauthrequired":
    case "authorized":
    case "pendingconf":
      return EncounterStatusV2.Open;
    default:
      return "";
  }
}

/** True when persisted clinical (or legacy encounter) status is Open — schedule list → encounter page. */
export function isEncounterClinicalStatusOpen(
  appt: Appointment | null | undefined
): boolean {
  if (!appt?.id) {
    return false;
  }
  const clinical = readClinicalStatusField(appt);
  if (clinical && normalizeScheduleStatusKey(clinical) === "open") {
    return true;
  }
  const legacy = readLegacyEncounterStatusField(appt);
  return !!legacy && normalizeScheduleStatusKey(legacy) === "open";
}

/** True when encounter is flagged or labeled retro authorization. */
export function isAppointmentRetroAuth(
  appt: Appointment | null | undefined
): boolean {
  if (!appt) {
    return false;
  }
  if (appt.isRetroAuth === true) {
    return true;
  }
  const clinicalOrLegacy =
    readClinicalStatusField(appt) ||
    readLegacyEncounterStatusField(appt) ||
    (appt.encounterStatus ?? "").trim();
  return normalizeScheduleStatusKey(clinicalOrLegacy) === "retroauth";
}

/** Resolve admissions schedule status from a list-clinic row (includes retro-auth flag). */
export function resolveAdmissionsScheduleStatusFromAppointment(
  appt: Appointment | null | undefined
): string {
  if (!appt) {
    return "";
  }
  if (appt.isRetroAuth === true) {
    return EncounterStatusV2.RetroAuth;
  }
  const clinicalOrEncounter =
    readClinicalStatusField(appt) || (appt.encounterStatus ?? "").trim();
  if (clinicalOrEncounter === EncounterStatus.RetroAuth) {
    return EncounterStatusV2.RetroAuth;
  }
  return normalizeAdmissionsScheduleStatusDisplay(
    getWorksheetStatusRawFromAppointment(appt)
  );
}

/** True when a status value belongs to the admissions schedule filter set. */
export function isAdmissionsScheduleStatusFilterValue(value: string): boolean {
  const normalized = normalizeScheduleStatusKey(value);
  return ADMISSIONS_SCHEDULE_STATUS_OPTIONS.some(
    (opt) => normalizeScheduleStatusKey(opt.value) === normalized
  );
}

/** Default sidebar filter tags on admissions schedule listing. */
export const ADMISSIONS_SCHEDULE_STATUS_DEFAULT_SELECTED: readonly string[] = [
  EncounterStatusV2.Scheduled,
  EncounterStatusV2.Open,
  EncounterStatusV2.RetroAuth,
  EncounterStatusV2.ReadyForBilling,
  EncounterStatus.Complete,
  EncounterStatusV2.Submitted,
  EncounterStatusV2.Waitlist,
];

export function isAdmissionsScheduleStatusSelectedByDefault(value: string): boolean {
  const normalized = normalizeScheduleStatusKey(value);
  return ADMISSIONS_SCHEDULE_STATUS_DEFAULT_SELECTED.some(
    (status) => normalizeScheduleStatusKey(status) === normalized
  );
}

/** @deprecated Use isAdmissionsScheduleStatusSelectedByDefault. */
export function isAdmissionsScheduleStatusExcludedByDefault(value: string): boolean {
  return !isAdmissionsScheduleStatusSelectedByDefault(value);
}

/** Confirmation toggle is for a DOS that has not passed. Today and later dates qualify. */
export function isFutureAppointmentDos(raw: string | null | undefined): boolean {
  const value = (raw ?? "").toString().trim();
  if (!value) {
    return false;
  }
  let dos = DateTime.fromISO(value);
  if (!dos.isValid) {
    dos = DateTime.fromJSDate(new Date(value));
  }
  if (!dos.isValid) {
    return false;
  }
  return dos.startOf("day") >= DateTime.now().startOf("day");
}
